/**
 * The KPI modal's preview: the metric live, with its filters, as the chosen
 * chart; quick ranges 7D / 30D / 3M / 6M / 12M / All time, Compare (None,
 * Last Period, Last Year) and a chart switcher. A saved KPI shows its status
 * card above the chart. The benchmark strip says honestly that benchmarks
 * are not available yet (they need cross-agency data, roadmap C9).
 */

import { useEffect, useState, type ReactNode } from 'react';

import { formatValue } from '../reports/format.ts';
import {
  reportsApi,
  type Catalogue,
  type CompareSetting,
  type RangeSetting,
  type Widget,
  type WidgetResult,
} from '../reports/reports-api.ts';
import { WidgetView } from '../reports/WidgetView.tsx';
import { conditionText, paceText, periodText, STATUS_LABEL, statusTone } from './kpi-format.ts';
import { KPI_VISUALS } from './KpiMetricPicker.tsx';
import type { Kpi, KpiDraft, KpiVisual } from './kpis-api.ts';

const QUICK_RANGES: [string, RangeSetting][] = [
  ['7D', { preset: 'LAST_7_DAYS' }],
  ['30D', { preset: 'LAST_30_DAYS' }],
  ['3M', { preset: 'LAST_N_MONTHS', n: 3 }],
  ['6M', { preset: 'LAST_N_MONTHS', n: 6 }],
  ['12M', { preset: 'LAST_N_MONTHS', n: 12 }],
  ['All time', { preset: 'ALL_TIME' }],
];
const COMPARES: [string, string][] = [
  ['NONE', 'None'],
  ['PREVIOUS_PERIOD', 'Last Period'],
  ['PREVIOUS_YEAR', 'Last Year'],
];
const BY_DIMENSION: KpiVisual[] = ['PIE', 'BAR', 'HORIZONTAL_BAR'];
const PREVIEW_HEIGHT = 300;

function previewWidget(draft: KpiDraft, compare: string): Widget {
  const dimension = BY_DIMENSION.includes(draft.visual) ? draft.secondary_dimension : null;
  return {
    id: 'preview',
    type: draft.visual,
    x: 0,
    y: 0,
    w: 12,
    h: 4,
    data: { source: draft.source, metrics: [draft.metric], filters: draft.filters, dimension },
    display: { title: draft.title, show_comparison: compare !== 'NONE', show_sparkline: true },
  };
}

function StatusCard({ kpi, catalogue }: { kpi: Kpi; catalogue: Catalogue }): ReactNode {
  const metric = catalogue.sources
    .find((source) => source.key === kpi.source)
    ?.metrics.find((item) => item.key === kpi.metric);
  if (!kpi.target_type || !kpi.last_status) return null;
  const tone = statusTone(kpi.last_status);
  return (
    <div className={`kp-status kp-status-${tone}`} role="status">
      <span>
        <small>Current</small>
        <strong>{formatValue(kpi.last_value, metric?.format ?? 'number')}</strong>
      </span>
      <span>
        <small>{STATUS_LABEL[kpi.last_status]}</small>
        <strong>{paceText(kpi) ?? '–'}</strong>
      </span>
      <span>
        <small>Target · {periodText(kpi)}</small>
        <strong>{conditionText(kpi.target_condition, kpi.target_value, metric)}</strong>
      </span>
    </div>
  );
}

export function KpiPreview(props: {
  organisationId: string;
  clientId: string;
  timezone: string;
  catalogue: Catalogue;
  draft: KpiDraft;
  kpi: Kpi | null;
  onVisual: (visual: KpiVisual) => void;
}): ReactNode {
  const [rangeIndex, setRangeIndex] = useState(1);
  const [compare, setCompare] = useState('NONE');
  const [result, setResult] = useState<WidgetResult | undefined>(undefined);
  const { draft } = props;
  const widget = previewWidget(draft, compare);
  const range = QUICK_RANGES[rangeIndex]?.[1] ?? { preset: 'LAST_30_DAYS' };
  const key = JSON.stringify([widget.data, range, compare, props.clientId]);
  useEffect(() => {
    let cancelled = false;
    setResult(undefined);
    const compareSetting: CompareSetting = { mode: compare };
    reportsApi
      .query(props.organisationId, {
        business_unit_id: props.clientId,
        mode: 'LIVE',
        timezone: props.timezone,
        range,
        compare: compareSetting,
        widgets: [{ id: 'preview', ...widget.data }],
      })
      .then(({ results }) => !cancelled && setResult(results[0]))
      .catch(() => !cancelled && setResult(undefined));
    return () => {
      cancelled = true;
    };
    // key captures every input of the query.
  }, [key, props.organisationId, props.timezone]);
  const source = props.catalogue.sources.find((item) => item.key === draft.source);

  return (
    <div className="kp-preview">
      {props.kpi && <StatusCard kpi={props.kpi} catalogue={props.catalogue} />}
      <div className="kp-preview-bar">
        <div className="rb-seg" role="radiogroup" aria-label="Preview period">
          {QUICK_RANGES.map(([label], index) => (
            <button
              key={label}
              type="button"
              role="radio"
              aria-checked={index === rangeIndex}
              className={index === rangeIndex ? 'on' : ''}
              onClick={() => setRangeIndex(index)}
            >
              {label}
            </button>
          ))}
        </div>
        <label className="kp-inline">
          Compare
          <select value={compare} onChange={(event) => setCompare(event.target.value)}>
            {COMPARES.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="kp-inline">
          Chart
          <select
            value={draft.visual}
            onChange={(event) => props.onVisual(event.target.value as KpiVisual)}
          >
            {KPI_VISUALS.map(([visual, label]) => (
              <option key={visual} value={visual}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div
        className="kp-chart"
        style={{ minHeight: PREVIEW_HEIGHT }}
        aria-busy={result === undefined}
      >
        {result === undefined ? (
          <p className="kp-faint">Loading the live numbers…</p>
        ) : (
          <WidgetView widget={widget} result={result} source={source} height={PREVIEW_HEIGHT} />
        )}
      </div>
      <p className="kp-benchmark">
        <strong>Benchmarks</strong> Period: L30D · Industry: All · Country: All —{' '}
        <span className="kp-faint">Benchmarks aren’t available for this metric yet.</span>
      </p>
    </div>
  );
}
